// src/pages/employee/EmployeeProfile.jsx
// ============================================================
// Employee Self Service (ESS) Profile Page.
// Allows employee to edit non-sensitive details directly.
// Sensitive updates are queued for HR Admin approval.
// Supports adding Education and Experience records.
// ============================================================

import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useEmployeePersonalDetails,
  useEmployeeBankDetails,
  useEmployeeEducation,
  useEmployeeExperience,
  useEmployeeTimeline,
  submitApprovalRequest,
} from "../../hooks/useEmployeeMaster";
import { useMyAssets } from "../../hooks/useAssets";
import {
  validatePAN,
  validateIFSC,
  validateMobile,
  validateEmergencyContact,
} from "../../utils/validation";
import { getNavItems, getPanelLabel } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { doc, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";


export default function EmployeeProfile() {
  const { userProfile } = useAuth();
  const uid = userProfile?.uid || userProfile?.id;

  // Real-time hooks from Employee Master module
  const { data: personal } = useEmployeePersonalDetails(uid);
  const { data: bank } = useEmployeeBankDetails(uid);
  const { data: education, add: addEdu, remove: removeEdu } = useEmployeeEducation(uid);
  const { data: experience, add: addExp, remove: removeExp } = useEmployeeExperience(uid);
  const { data: timeline } = useEmployeeTimeline(uid);
  const { data: myAssets, loading: assetsLoading } = useMyAssets(uid);


  const [activeSubTab, setActiveSubTab] = useState("general");

  const [form, setForm] = useState({
    personalEmail: "",
    phone: "",
    alternateMobile: "",
    emergencyContact: "",
    fatherName: "",
    motherName: "",
    spouseName: "",
    aadhaar: "",
    pan: "",
    passport: "",
    bankName: "",
    bankBranch: "",
    accountNumber: "",
    ifscCode: "",
    accountHolder: "",
    upiId: "",
  });

  // Load data into local state once loaded from Firestore
  useEffect(() => {
    if (userProfile) {
      setForm((p) => ({
        ...p,
        personalEmail: userProfile.personalEmail || "",
        phone: userProfile.phone || "",
        alternateMobile: userProfile.alternateMobile || "",
        emergencyContact: userProfile.emergencyContact || "",
      }));
    }
  }, [userProfile]);

  useEffect(() => {
    if (personal) {
      setForm((p) => ({
        ...p,
        fatherName: personal.fatherName || "",
        motherName: personal.motherName || "",
        spouseName: personal.spouseName || "",
        aadhaar: personal.aadhaar || "",
        pan: personal.pan || "",
        passport: personal.passport || "",
      }));
    }
  }, [personal]);

  useEffect(() => {
    if (bank) {
      setForm((p) => ({
        ...p,
        bankName: bank.bankName || "",
        bankBranch: bank.branch || "",
        accountNumber: bank.accountNumber || "",
        ifscCode: bank.ifscCode || "",
        accountHolder: bank.accountHolder || "",
        upiId: bank.upiId || "",
      }));
    }
  }, [bank]);

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // Subform states for adding Experience/Education
  const [showEduForm, setShowEduForm] = useState(false);
  const [eduData, setEduData] = useState({ qualification: "", college: "", university: "", passingYear: "", grade: "", percentage: "" });

  const [showExpForm, setShowExpForm] = useState(false);
  const [expData, setExpData] = useState({ company: "", designation: "", startDate: "", endDate: "", yearsOfExperience: "", lastSalary: "", reasonForLeaving: "" });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setErr("");

    try {
      const sensitiveUpdates = {};
      const directUserUpdates = {};
      const directPersonalUpdates = {};
      const directBankUpdates = {};

      // 1. Compare and extract sensitive updates (require approval)
      if (form.aadhaar !== (personal?.aadhaar || "")) sensitiveUpdates.aadhaar = form.aadhaar;
      if (form.pan !== (personal?.pan || "")) {
        if (form.pan && !validatePAN(form.pan)) throw new Error("Invalid PAN Card format (AAAAA9999A)");
        sensitiveUpdates.pan = form.pan;
      }
      if (form.passport !== (personal?.passport || "")) sensitiveUpdates.passport = form.passport;
      if (form.bankName !== (bank?.bankName || "")) sensitiveUpdates.bankName = form.bankName;
      if (form.bankBranch !== (bank?.branch || "")) sensitiveUpdates.bankBranch = form.bankBranch;
      if (form.accountNumber !== (bank?.accountNumber || "")) sensitiveUpdates.accountNumber = form.accountNumber;
      if (form.ifscCode !== (bank?.ifscCode || "")) {
        if (form.ifscCode && !validateIFSC(form.ifscCode)) throw new Error("Invalid bank IFSC format");
        sensitiveUpdates.ifscCode = form.ifscCode;
      }
      if (form.accountHolder !== (bank?.accountHolder || "")) sensitiveUpdates.accountHolder = form.accountHolder;

      // 2. Extract direct non-sensitive updates (commit immediately)
      if (form.personalEmail !== (userProfile.personalEmail || "")) directUserUpdates.personalEmail = form.personalEmail;
      if (form.phone !== (userProfile.phone || "")) {
        if (form.phone && !validateMobile(form.phone)) throw new Error("Invalid mobile number format");
        directUserUpdates.phone = form.phone;
      }
      if (form.alternateMobile !== (userProfile.alternateMobile || "")) {
        if (form.alternateMobile && !validateMobile(form.alternateMobile)) throw new Error("Invalid alternate mobile format");
        directUserUpdates.alternateMobile = form.alternateMobile;
      }
      if (form.emergencyContact !== (userProfile.emergencyContact || "")) {
        if (form.emergencyContact && !validateMobile(form.emergencyContact)) throw new Error("Invalid contact format");
        if (form.emergencyContact && !validateEmergencyContact(form.emergencyContact, form.phone, form.alternateMobile)) {
          throw new Error("Emergency contact number must differ from your mobile numbers");
        }
        directUserUpdates.emergencyContact = form.emergencyContact;
      }

      if (form.fatherName !== (personal?.fatherName || "")) directPersonalUpdates.fatherName = form.fatherName;
      if (form.motherName !== (personal?.motherName || "")) directPersonalUpdates.motherName = form.motherName;
      if (form.spouseName !== (personal?.spouseName || "")) directPersonalUpdates.spouseName = form.spouseName;

      if (form.upiId !== (bank?.upiId || "")) directBankUpdates.upiId = form.upiId;

      // 3. Batch commit direct updates
      const promises = [];
      if (Object.keys(directUserUpdates).length > 0) {
        promises.push(updateDoc(doc(db, "users", uid), { ...directUserUpdates, updatedAt: serverTimestamp() }));
      }
      if (Object.keys(directPersonalUpdates).length > 0) {
        promises.push(setDoc(doc(db, "users", uid, "personal_details", "main"), { ...directPersonalUpdates, updatedAt: serverTimestamp() }, { merge: true }));
      }
      if (Object.keys(directBankUpdates).length > 0) {
        promises.push(setDoc(doc(db, "users", uid, "bank_details", "main"), { ...directBankUpdates, updatedAt: serverTimestamp() }, { merge: true }));
      }

      // 4. Submit approval requests if any sensitive fields were modified
      if (Object.keys(sensitiveUpdates).length > 0) {
        await submitApprovalRequest({
          employeeId: uid,
          requestedBy: uid,
          requestedByName: userProfile.displayName,
          role: userProfile.role,
          fields: sensitiveUpdates,
        });
        setMsg("✅ Profile saved. Sensitive updates (Bank/ID) are queued for approval by HR Manager.");
      } else {
        setMsg("✅ Profile details updated successfully.");
      }

      await Promise.all(promises);
      setTimeout(() => setMsg(""), 4000);
    } catch (err) {
      setErr(err.message || "Failed to update profile.");
      setTimeout(() => setErr(""), 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleAddEdu = async (e) => {
    e.preventDefault();
    if (!eduData.qualification || !eduData.college || !eduData.passingYear) return;
    try {
      await addEdu({
        qualification: eduData.qualification,
        college: eduData.college,
        university: eduData.university,
        passingYear: parseInt(eduData.passingYear),
        percentage: eduData.percentage || "",
        grade: eduData.grade || "",
      });
      setShowEduForm(false);
      setEduData({ qualification: "", college: "", university: "", passingYear: "", grade: "", percentage: "" });
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddExp = async (e) => {
    e.preventDefault();
    if (!expData.company || !expData.designation || !expData.startDate) return;
    try {
      await addExp({
        company: expData.company,
        designation: expData.designation,
        startDate: expData.startDate,
        endDate: expData.endDate || "Present",
        yearsOfExperience: parseFloat(expData.yearsOfExperience) || 0,
        lastSalary: parseFloat(expData.lastSalary) || 0,
        reasonForLeaving: expData.reasonForLeaving || "",
      });
      setShowExpForm(false);
      setExpData({ company: "", designation: "", startDate: "", endDate: "", yearsOfExperience: "", lastSalary: "", reasonForLeaving: "" });
    } catch (err) {
      console.error(err);
    }
  };

  const navItems = getNavItems(userProfile);
  const panelLabel = getPanelLabel(userProfile);

  return (
    <Layout navItems={navItems} panelLabel={panelLabel}>
      <style>{`
        .profile-container {
          display: grid;
          grid-template-columns: 280px 1fr;
          gap: 1.5rem;
          margin-top: 1.5rem;
        }
        .profile-sidebar {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        .profile-avatar-large {
          width: 90px;
          height: 90px;
          border-radius: 50%;
          background: var(--brand-primary, #1b4b31);
          color: white;
          font-size: 2.25rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1rem;
          box-shadow: 0 4px 10px rgba(0,0,0,0.15);
        }
        .profile-username {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }
        .profile-role-badge {
          background: rgba(27, 75, 49, 0.1);
          color: var(--brand-primary, #1b4b31);
          font-size: 0.72rem;
          font-weight: 600;
          padding: 0.25rem 0.6rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
        }
        .profile-nav-list {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          border-top: 1px solid var(--border);
          padding-top: 1rem;
        }
        .profile-nav-btn {
          width: 100%;
          text-align: left;
          padding: 0.6rem 0.75rem;
          background: transparent;
          border: none;
          color: var(--text-muted);
          font-weight: 500;
          font-size: 0.85rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s ease;
        }
        .profile-nav-btn--active {
          background: rgba(27, 75, 49, 0.08);
          color: var(--brand-primary, #1b4b31);
        }
        .subform-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .profile-item-box {
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 0.875rem;
          background: var(--bg-secondary);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
      `}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">My Profile</h1>
          <p className="page-subtitle">Manage your personal master registry details and statutory updates</p>
        </div>
      </div>

      {msg && <div className="form-success">{msg}</div>}
      {err && <div className="form-error">{err}</div>}

      <div className="profile-container">
        {/* Sidebar */}
        <div className="profile-sidebar">
          <div className="profile-avatar-large">
            {userProfile?.displayName?.slice(0, 2).toUpperCase()}
          </div>
          <div className="profile-username">{userProfile?.displayName}</div>
          <div className="profile-role-badge">
            {userProfile?.role === "admin" ? "HR Admin" : userProfile?.role === "lead" ? "Team Lead" : "Employee"}
          </div>

          <div className="profile-nav-list">
            <button type="button" className={`profile-nav-btn ${activeSubTab === "general" ? "profile-nav-btn--active" : ""}`} onClick={() => setActiveSubTab("general")}>
              Personal & Bank Details
            </button>
            <button type="button" className={`profile-nav-btn ${activeSubTab === "performance" ? "profile-nav-btn--active" : ""}`} onClick={() => setActiveSubTab("performance")}>
              🎯 Performance Overview
            </button>
            <button
              type="button"
              className={`profile-nav-btn ${activeSubTab === "assets" ? "profile-nav-btn--active" : ""}`}
              onClick={() => setActiveSubTab("assets")}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            >
              <span>💻 My Assets</span>
              {myAssets.length > 0 && (
                <span style={{
                  background: "var(--brand-primary, #1b4b31)",
                  color: "#fff",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  borderRadius: 99,
                  padding: "1px 7px",
                  minWidth: 18,
                  textAlign: "center",
                }}>{myAssets.length}</span>
              )}
            </button>
            <button type="button" className={`profile-nav-btn ${activeSubTab === "education" ? "profile-nav-btn--active" : ""}`} onClick={() => setActiveSubTab("education")}>
              Academic Profile
            </button>
            <button type="button" className={`profile-nav-btn ${activeSubTab === "experience" ? "profile-nav-btn--active" : ""}`} onClick={() => setActiveSubTab("experience")}>
              Prior Work Experience
            </button>
            <button type="button" className={`profile-nav-btn ${activeSubTab === "timeline" ? "profile-nav-btn--active" : ""}`} onClick={() => setActiveSubTab("timeline")}>
              Activity History
            </button>
          </div>

        </div>

        {/* Content Panel */}
        <div className="form-card" style={{ height: "fit-content" }}>
          
          {activeSubTab === "general" && (
            <form onSubmit={handleSaveGeneral} className="form-grid">
              
              <div className="form-group form-group--full">
                <h4 style={{ margin: "0 0 1rem 0", borderBottom: "1px solid var(--border)", paddingBottom: "0.5rem" }}>
                  📞 Contact & Personal Details (Direct Commits)
                </h4>
              </div>
              
              <div className="form-group">
                <label>Personal Email</label>
                <input name="personalEmail" type="email" value={form.personalEmail} onChange={handleInputChange} placeholder="username@gmail.com" />
              </div>
              <div className="form-group">
                <label>Primary Phone *</label>
                <input name="phone" value={form.phone} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Alternate Mobile</label>
                <input name="alternateMobile" value={form.alternateMobile} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Emergency Contact Number *</label>
                <input name="emergencyContact" value={form.emergencyContact} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Father's Name</label>
                <input name="fatherName" value={form.fatherName} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Mother's Name</label>
                <input name="motherName" value={form.motherName} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Spouse Name</label>
                <input name="spouseName" value={form.spouseName} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>UPI ID</label>
                <input name="upiId" value={form.upiId} onChange={handleInputChange} placeholder="username@upi" />
              </div>

              <div className="form-group form-group--full" style={{ marginTop: "1.5rem" }}>
                <h4 style={{ margin: "0 0 0.5rem 0", borderBottom: "1px solid var(--border)", paddingBottom: "0.5rem" }}>
                  🔒 Sensitive Identification & Bank Settings (Requires HR Approval)
                </h4>
                <p className="text-muted" style={{ fontSize: "0.78rem", marginBottom: "1rem" }}>
                  Modifying any fields below will trigger an approval request. Values will update once authorized by HR.
                </p>
              </div>

              <div className="form-group">
                <label>Aadhaar Card Number</label>
                <input name="aadhaar" value={form.aadhaar} onChange={handleInputChange} placeholder="12 Digit Aadhaar" />
              </div>
              <div className="form-group">
                <label>PAN Card Number</label>
                <input name="pan" value={form.pan} onChange={handleInputChange} placeholder="ABCDE1234F" />
              </div>
              <div className="form-group">
                <label>Passport Number</label>
                <input name="passport" value={form.passport} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Bank Name</label>
                <input name="bankName" value={form.bankName} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Bank Branch</label>
                <input name="bankBranch" value={form.bankBranch} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Account Number</label>
                <input name="accountNumber" value={form.accountNumber} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>IFSC Code</label>
                <input name="ifscCode" value={form.ifscCode} onChange={handleInputChange} placeholder="HDFC0001234" />
              </div>
              <div className="form-group">
                <label>Account Holder Name</label>
                <input name="accountHolder" value={form.accountHolder} onChange={handleInputChange} />
              </div>

              <div className="form-group form-group--full" style={{ marginTop: "1rem" }}>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving Changes…" : "Save Profile Details"}
                </button>
              </div>

            </form>
          )}

          {/* TAB 2: EDUCATION */}
          {activeSubTab === "education" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                <h4 style={{ margin: 0 }}>Academic Credentials</h4>
                <button className="btn-primary-sm" onClick={() => setShowEduForm(!showEduForm)}>
                  {showEduForm ? "✕ Cancel" : "+ Add Degree"}
                </button>
              </div>

              {showEduForm && (
                <form onSubmit={handleAddEdu} className="form-card" style={{ background: "var(--bg-secondary)", padding: "1rem", marginBottom: "1.5rem" }}>
                  <div className="subform-row">
                    <div className="form-group">
                      <label>Degree / Qualification *</label>
                      <input value={eduData.qualification} onChange={(e) => setEduData(p => ({ ...p, qualification: e.target.value }))} placeholder="e.g. B.Tech" required />
                    </div>
                    <div className="form-group">
                      <label>Institution / College *</label>
                      <input value={eduData.college} onChange={(e) => setEduData(p => ({ ...p, college: e.target.value }))} placeholder="CBIT" required />
                    </div>
                  </div>
                  <div className="subform-row">
                    <div className="form-group">
                      <label>University / Board *</label>
                      <input value={eduData.university} onChange={(e) => setEduData(p => ({ ...p, university: e.target.value }))} placeholder="JNTUH" required />
                    </div>
                    <div className="form-group">
                      <label>Passing Year *</label>
                      <input type="number" value={eduData.passingYear} onChange={(e) => setEduData(p => ({ ...p, passingYear: e.target.value }))} placeholder="2020" required />
                    </div>
                  </div>
                  <div className="subform-row">
                    <div className="form-group">
                      <label>Percentage (%)</label>
                      <input value={eduData.percentage} onChange={(e) => setEduData(p => ({ ...p, percentage: e.target.value }))} placeholder="82.4" />
                    </div>
                    <div className="form-group">
                      <label>Grade / CGPA</label>
                      <input value={eduData.grade} onChange={(e) => setEduData(p => ({ ...p, grade: e.target.value }))} placeholder="8.4" />
                    </div>
                  </div>
                  <button type="submit" className="btn-success-sm" style={{ marginTop: "0.5rem" }}>Save Record</button>
                </form>
              )}

              {education.length === 0 ? <p className="text-muted">No academic records logged yet.</p> : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {education.map(e => (
                    <div key={e.id} className="profile-item-box">
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{e.qualification}</div>
                        <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{e.college} ({e.university})</div>
                        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "4px" }}>
                          Passed: {e.passingYear} • {e.percentage ? `${e.percentage}%` : e.grade}
                        </div>
                      </div>
                      <button className="btn-danger-sm" style={{ padding: "0.25rem 0.5rem" }} onClick={() => removeEdu(e.id)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: EXPERIENCE */}
          {activeSubTab === "experience" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                <h4 style={{ margin: 0 }}>Professional History</h4>
                <button className="btn-primary-sm" onClick={() => setShowExpForm(!showExpForm)}>
                  {showExpForm ? "✕ Cancel" : "+ Add Experience"}
                </button>
              </div>

              {showExpForm && (
                <form onSubmit={handleAddExp} className="form-card" style={{ background: "var(--bg-secondary)", padding: "1rem", marginBottom: "1.5rem" }}>
                  <div className="subform-row">
                    <div className="form-group">
                      <label>Previous Employer *</label>
                      <input value={expData.company} onChange={(e) => setExpData(p => ({ ...p, company: e.target.value }))} placeholder="InfoBridge Inc." required />
                    </div>
                    <div className="form-group">
                      <label>Designation / Role *</label>
                      <input value={expData.designation} onChange={(e) => setExpData(p => ({ ...p, designation: e.target.value }))} placeholder="Software Engineer" required />
                    </div>
                  </div>
                  <div className="subform-row">
                    <div className="form-group">
                      <label>Start Date *</label>
                      <input type="date" value={expData.startDate} onChange={(e) => setExpData(p => ({ ...p, startDate: e.target.value }))} required />
                    </div>
                    <div className="form-group">
                      <label>End Date (Blank if current)</label>
                      <input type="date" value={expData.endDate} onChange={(e) => setExpData(p => ({ ...p, endDate: e.target.value }))} />
                    </div>
                  </div>
                  <div className="subform-row">
                    <div className="form-group">
                      <label>Years of Experience</label>
                      <input type="number" step="0.1" value={expData.yearsOfExperience} onChange={(e) => setExpData(p => ({ ...p, yearsOfExperience: e.target.value }))} placeholder="2.5" />
                    </div>
                    <div className="form-group">
                      <label>Last Drawn CTC (INR)</label>
                      <input type="number" value={expData.lastSalary} onChange={(e) => setExpData(p => ({ ...p, lastSalary: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-group form-group--full">
                    <label>Reason for Leaving</label>
                    <input value={expData.reasonForLeaving} onChange={(e) => setExpData(p => ({ ...p, reasonForLeaving: e.target.value }))} />
                  </div>
                  <button type="submit" className="btn-success-sm" style={{ marginTop: "0.5rem" }}>Save Experience</button>
                </form>
              )}

              {experience.length === 0 ? <p className="text-muted">No prior work experience logged.</p> : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {experience.map(e => (
                    <div key={e.id} className="profile-item-box">
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{e.designation}</div>
                        <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{e.company}</div>
                        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "4px" }}>
                          {e.startDate} to {e.endDate || "Present"} ({e.yearsOfExperience} years)
                        </div>
                      </div>
                      <button className="btn-danger-sm" style={{ padding: "0.25rem 0.5rem" }} onClick={() => removeExp(e.id)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}


          {/* TAB: MY ASSETS */}
          {activeSubTab === "assets" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
                <h4 style={{ margin: 0 }}>💻 Assigned Assets</h4>
                <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                  {myAssets.length} asset{myAssets.length !== 1 ? "s" : ""} assigned to you
                </span>
              </div>

              {assetsLoading ? (
                <div style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem 0" }}>Loading assets…</div>
              ) : myAssets.length === 0 ? (
                <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: "var(--text-muted)" }}>
                  <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>📦</div>
                  <div style={{ fontWeight: 700, fontSize: "1rem", color: "var(--text-primary)", marginBottom: "0.35rem" }}>No assets assigned</div>
                  <div style={{ fontSize: "0.82rem" }}>Assets assigned to you by HR will appear here.</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
                  {myAssets.map((asset) => {
                    const assignedDate = asset.assignedAt
                      ? (asset.assignedAt.toDate
                          ? asset.assignedAt.toDate()
                          : new Date(asset.assignedAt)
                        ).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                      : null;
                    return (
                      <div
                        key={asset.id}
                        style={{
                          border: "1px solid var(--border)",
                          borderLeft: "4px solid #10b981",
                          borderRadius: 10,
                          padding: "1rem 1.1rem",
                          background: "var(--bg-secondary)",
                          display: "grid",
                          gridTemplateColumns: "1fr auto",
                          gap: "0.75rem",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.35rem", flexWrap: "wrap" }}>
                            <span style={{
                              fontFamily: "monospace",
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              color: "#1b4b31",
                              background: "rgba(27,75,49,0.08)",
                              padding: "2px 7px",
                              borderRadius: 5,
                            }}>
                              {asset.assetTag || "—"}
                            </span>
                            <span style={{
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              background: "#dbeafe",
                              color: "#1e40af",
                              padding: "2px 8px",
                              borderRadius: 99,
                            }}>
                              {asset.category}
                            </span>
                          </div>
                          <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.2rem" }}>
                            {asset.name}
                          </div>
                          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                            {[asset.brand, asset.model].filter(Boolean).join(" ") || "—"}
                            {asset.serialNumber ? ` · S/N: ${asset.serialNumber}` : ""}
                          </div>
                          {assignedDate && (
                            <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginTop: "0.3rem" }}>
                              📅 Assigned on {assignedDate}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <span style={{
                            display: "inline-block",
                            padding: "3px 10px",
                            borderRadius: 99,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            background: "#d1fae5",
                            color: "#065f46",
                          }}>
                            ✔ In Use
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 4: TIMELINE */}

          {activeSubTab === "timeline" && (
            <div>
              <h4 style={{ marginBottom: "1.5rem" }}>Profile Timeline Activity</h4>
              {timeline.length === 0 ? <p className="text-muted">No events logged.</p> : (
                <div className="history-timeline" style={{ paddingLeft: "1.25rem", borderLeft: "2px solid var(--border)" }}>
                  {timeline.map((event) => (
                    <div key={event.id} className="timeline-event" style={{ position: "relative", marginBottom: "1rem" }}>
                      <div className="timeline-dot" style={{ position: "absolute", left: "calc(-1.25rem - 6px)", top: "4px", width: "10px", height: "10px", borderRadius: "50%", background: "var(--brand-primary, #1b4b31)", border: "2px solid var(--bg-primary)" }} />
                      <div className="timeline-time" style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
                        {event.timestamp ? (event.timestamp.toDate ? event.timestamp.toDate().toLocaleDateString() : new Date(event.timestamp).toLocaleDateString()) : "—"}
                      </div>
                      <div className="timeline-title" style={{ fontSize: "0.8rem", fontWeight: 600 }}>{event.title}</div>
                      <div className="timeline-desc" style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{event.description}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 5: PERFORMANCE OVERVIEW */}
          {activeSubTab === "performance" && (
            <div>
              <h4 style={{ marginBottom: "1rem" }}>Employee Performance Summary</h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
                <div style={{ padding: "0.85rem", background: "var(--bg-secondary)", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Current Rating</div>
                  <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#1b4b31" }}>4.3 / 5.0</div>
                </div>
                <div style={{ padding: "0.85rem", background: "var(--bg-secondary)", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Goal Progress</div>
                  <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#0284c7" }}>80%</div>
                </div>
                <div style={{ padding: "0.85rem", background: "var(--bg-secondary)", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Appraisal Status</div>
                  <span className="badge badge-success" style={{ marginTop: "4px" }}>Active Review</span>
                </div>
              </div>

              <h5 style={{ marginBottom: "0.75rem" }}>Assigned Goals</h5>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1.5rem" }}>
                <div className="profile-item-box">
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>Deliver Q3 Architecture & API Upgrades</div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "2px" }}>Priority: High • Progress: 80%</div>
                  </div>
                  <span className="badge badge-info">In Progress</span>
                </div>
              </div>

              <h5 style={{ marginBottom: "0.75rem" }}>Performance History & Ratings</h5>
              <div style={{ fontSize: "0.82rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem", background: "var(--bg-secondary)", borderRadius: "6px" }}>
                  <span>Q2 FY2026 Appraisal</span>
                  <strong style={{ color: "#d97706" }}>4.5 ★ (Exceeds Expectations)</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem", background: "var(--bg-secondary)", borderRadius: "6px" }}>
                  <span>Q1 FY2026 Appraisal</span>
                  <strong style={{ color: "#d97706" }}>4.2 ★ (Meets Expectations)</strong>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
}
